/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import * as localStorage from './localStorage';
import * as clientRecords from './clientRecords';
import * as valueStatus from './valueStatuses';
import * as session from './session';
import * as views from './views';
import {categories} from './categories';
import * as pendingChanges from './pendingChanges';
import {properties} from './properties';
import {recordTypes} from './recordTypes';
import * as securities from './securities';
import * as securityGroups from './securityGroups';
import * as securityGroupLookups from './securityGroupLookups';
import * as packages from './packages';
import * as styles from './styles';
import * as tags from './tags';
import * as extensions from './extensions';
import * as snapshot from './snapshot';
import {Dictionary} from "../utils/Dictionary";
import {noop} from '../utils/tools';
import {Style} from "../entities/style";

/**
 * @callback MergeEntityCallback
 * @param {EntityBase} entity - The entity that was merged
 */

class Cache {
    /**
     * Create an instance of the application-level cache
     */
    constructor () {

        //#region entity cache objects

        /**
         * The properties cache
         * @type {PropertyCache}
         */
        this.properties = properties;

        this.categories = categories;
        this.recordTypes = recordTypes;
        this.views = views;
        this.valueStatuses = valueStatus;
        this.styles = styles;
        this.securities = securities;
        this.securityGroups = securityGroups;
        this.securityGroupLookups = securityGroupLookups;
        this.tags = tags;
        this.extensions = extensions.extensionCache;
        this.pendingChanges = pendingChanges;
        this.packages = packages;

        //#endregion

        this.session = session;

        this.localStorage = localStorage;

        /**
         *
         * @type {{isActive?: boolean}}
         */
        this.snapshot = snapshot;

        // #region Stateful objects / collections

        this.clientRecords = clientRecords;
        this.mvcs = new Dictionary();
        this.dropDownMVCs = new Dictionary();

        // #endregion

        /**
         * Flag to indicate if the cache is initialized
         * @type {boolean}
         * @protected
         */
        this._isInitialized = false;

        /**
         * Flag to indicate if the app context is loaded
         * @type {boolean}
         * @protected
         */
        this._isAppContextLoaded = false;

    }

    //#region Class Properties

    /**
     * Get the flag that indicates if the cache is initialized
     * @returns {boolean}
     */
    get isInitialized () {
        return this._isInitialized;
    }

    /**
     * Set the flag that indicates if the cache is initialized
     * @param {boolean} value
     */
    set isInitialized (value) {
        this._isInitialized = value;

        if (value && this.isInitializedSub != null) {
            this.isInitializedSub();
            this.isInitializedSub = null;
        }
    }

    /**
     * Get the flag that indicates if the app context is loaded
     * @returns {boolean}
     */
    get isAppContextLoaded () {
        return this._isAppContextLoaded;
    }

    /**
     * Set the flag that indicates if the app context is loaded
     * @param {boolean} value
     */
    set isAppContextLoaded (value) {
        this._isAppContextLoaded = value;

        if (value && this.isAppContextLoadedSub != null) {
            this.isAppContextLoadedSub();
            this.isAppContextLoadedSub = null;
        }
    }

    //#endregion

    /**
     * Optional function to run when cache is initialized
     * @abstract
     */
    isInitializedSub () {
    }

    /**
     * Optional function to run when the app context is loaded
     * @abstract
     */
    isAppContextLoadedSub () {
    }

    /**
     * Connects to the WebSocket backend, then loads session config
     * @returns {Promise}
     */
    async preLoad () {
        // Call this so that we load the login box AFTER the app context is loaded (to see if we have 'remember me' users stored)...AL
        this._isAppContextLoaded = true;

        if (this.session.appContext == null) {
            this.session.appContext = {};
        }

        try {
            return await this.session.loadConfig();
        }
        catch (err) {
            app.dv.actionLibrary.executeShowMessage(null, null, null, "Lost connection to service due to inactivity or network issues. Refresh to attempt to connect again.", true, null, false);
            throw err;
        }
    }

    /**
     * Organizes the tags on the entity's client properties and the entity on the tag's client properties
     * @param {EntityBase} entity
     */
    organizeTagsForEntity (entity) {
        // populate the tags for this entity based on taglinks for the entity.
        this.tags.allTagLinks.Values.forEach((tagLink) => {
            if (tagLink.EntityCache === entity.UID) {
                let tag = this.tags.getTagByUID(tagLink.TagUID);
                if (tag != null) {
                    entity.clientProperties.Tags.add(tag.TagID, tag, tag.UID);
                    tag.clientProperties.taggedEntities.add(entity.UID, entity);
                }
            }
        });
    }

    /**
     * This will organize the cache by linking parent and child entities where appropriate...AL
     */
    organizeEntityCache () {
        //Apply taggedEntities dictionary to tags...KB
        this.tags.allTags.forEach(
            /**
             * Update the tag entity
             * @param {EntityBase} tag
             */
            (tag) => {
                if (tag.clientProperties == null) {
                    this.tags.applyClientProperties(tag);
                }
            });

        /**
         * Array of tag link entities for use later
         * @type {Array}
         */
        let allTagLinks = this.tags.allTagLinks.Values;

        // Ensure that record type entities have tag references updated
        this.recordTypes.allRecordTypes.forEach(
            /**
             * Update the Record Type entity
             * @param {RecordTypeEntity} recordType
             */
            (recordType) => {
                this.organizeTagsForEntity(recordType);
            });

        // Ensure that category entities have client properties and have tag references updated
        this.categories.allCategories.forEach(
            /**
             * Update the Category entity
             * @param {CategoryEntity} category
             */
            (category) => {
                this.organizeTagsForEntity(category);
            });

        // Ensure that property entities have client properties and have tag references updated
        this.properties.allProperties.forEach(
            /**
             * Update the property entity
             * @param {PropertyEntity} property
             */
            (property) => {
                this.organizeTagsForEntity(property);
            });

        // Ensure that view entities have client properties and have tag references updated
        this.views.allViews.forEach(
            /**
             * Update the view entity
             * @param {View} view
             */
            (view) => {
                if (view.clientProperties == null) {
                    this.views.applyClientProperties(view);
                }

                this.organizeTagsForEntity(view);

                // if this is a pending view, we need to keep the clientProperties in sync.
                /**
                 * Check for and update any original view entity for this view entity
                 * @type {View}
                 */
                let origView = this.views.allOriginalViews.getVal(view.ViewID);
                if (origView != null) {
                    origView.clientProperties = view.clientProperties;
                }
            });

        // Ensure that security group entities have client properties and have tag references updated
        this.securityGroups.allSecurityGroups.forEach(
            /**
             * Update the security group entity
             * @param {EntityBase} securityGroup
             */
            (securityGroup) => {
                if (securityGroup.clientProperties == null) {
                    this.securityGroups.applyClientProperties(securityGroup);
                }

                this.organizeTagsForEntity(securityGroup);
            });

        // Ensure that value status entities have client properties and have tag references updated
        this.valueStatuses.allValueStatuses.forEach(
            /**
             * Update the value status entity
             * @param {EntityBase} valueStatus
             */
            (valueStatus) => {
                if (valueStatus.clientProperties == null) {
                    this.valueStatuses.applyClientProperties(valueStatus);
                }

                this.organizeTagsForEntity(valueStatus);
            });

        // Ensure that style entities have client properties and have tag references updated
        this.styles.allStyles.forEach(
            /**
             * Update the style entity
             * @param {Style} styleEntity
             */
            (styleEntity) => {
                if (styleEntity.clientProperties == null) {
                    this.styles.applyClientProperties(styleEntity);
                }

                this.organizeTagsForEntity(styleEntity);
            });

        // Ensure that package entities have client properties and have tag references updated
        this.packages.allPackages.forEach(
            /**
             * Update the package entity
             * @param {EntityBase} packageEntity
             */
            (packageEntity) => {
                if (packageEntity.clientProperties == null) {
                    this.packages.applyClientProperties(packageEntity);
                }

                //NOTE: there are NO tags for packages - you can't package a package!
            });

        // Ensure that tag entities have client properties and have tag references updated
        this.tags.allTags.forEach(
            /**
             * Update the tag entity
             * @param {EntityBase} tag
             */
            (tag) => {
                if (tag.Definition.Name === "" || tag.Definition.Name == null) {
                    tag.Name = tag.Definition.Name = "<blankTag>";
                }

                /**
                 * Array of tag links for the tag entity
                 * @type {Array}
                 */
                let entityTagLinks = allTagLinks.filter(e => e.EntityUID === tag.UID);

                entityTagLinks.forEach(
                    /**
                     * Update the tag for each tag link
                     * @param {TagLink} taglink
                     */
                    (taglink) => {
                        /**
                         * Get the tag entity for the tag link tag UID
                         * @type {EntityBase}
                         */
                        let addTag = this.tags.getTagByUID(taglink.TagUID);

                        if (tag != null && addTag != null) {
                            tag.clientProperties.Tags.add(addTag.TagID, addTag, addTag.UID);
                            addTag.clientProperties.taggedEntities.add(tag.UID, tag);
                        }
                        else {
                            if (addTag == null) {
                                console.log("Please review taglinks. Taglink references deleted tag. Taglink ID = " + taglink.TagLinkID);
                            }
                        }
                    });
            });

        // Ensure that registered expression entities have client properties and have tag references updated
        this.extensions.registeredExpressions.forEach(
            /**
             * Update the registered expression entity
             * @param {EntityBase} exp
             */
            (exp) => {
                this.extensions.applyExpressionClientProperties(exp);
                this.organizeTagsForEntity(exp);
            });

        // Ensure that action entities have client properties and have tag references updated
        this.extensions.allActions.Values.forEach(
            /**
             * Update the action entity
             * @param {EntityBase} action
             */
            (action) => {
                this.extensions.applyActionClientProperties(action);
                this.organizeTagsForEntity(action);
            });

        // Ensure that widget entities have client properties and have tag references updated
        this.extensions.allWidgets.Values.forEach(
            /**
             * Update the widget entity
             * @param {EntityBase} widget
             */
            (widget) => {
                this.extensions.applyWidgetClientProperties(widget);
                this.organizeTagsForEntity(widget);
            });

        // Ensure that custom endpoint entities have client properties and have tag references updated
        this.extensions.allCustomEndpoints.Values.forEach(
            /**
             * Update the custom endpoint entity
             * @param {EntityBase} endpoint
             */
            (endpoint) => {
                this.extensions.applyEndpointClientProperties(endpoint);
                this.organizeTagsForEntity(endpoint);
                // We are not going to register the endpoint here so that designers are not confused since endpoint entities will not be pulled down by non-designers.
                // if (endpoint.Definition.IsPublished) {
                //     c.extensions.registerEndpoint(endpoint);
                // }
            });

        // Pending changes MUST be done last so that the original entities all exist in cache...WR
        this.pendingChanges.allPendingChanges.Values.forEach(
            /**
             * Update the pending change entity
             * @param {EntityBase} change
             */
            (change) => {
                this.pendingChanges.applyClientProperties(change);
                // NOTE: There are currently no "Tags" for pending changes, but may be used in the future.

                // update the changed entity's client properties.
                switch (change.TableName) {
                    case "View":
                        let viewRef = this.views.allViews.getVal(change.EntityID);
                        if (viewRef == null) {
                            console.warn(`Found pending change for form entity ${change.EntityID} that was not found in cache.`);
                        }
                        break;

                    case "Property":
                    case "RecordType":
                    case "Style":
                    case "Category":
                    case "ValueStatus":
                    case "Tag":
                    default:
                        throw new Error(`Pending change for ${change.TableName} not currently supported.`);
                }
            });

    }

    /**
     * Clear out all active instances of data in the application
     * @param {Function} [callback = noop]
     */
    unload (callback = noop) {
        //TODO: for the time being, clear the dictionary, but later we need to flush them to ensure information is saved...WR
        this.clientRecords.allClientRecords.clear();
        this.mvcs.clear();
        this.dropDownMVCs.clear();

        callback();
    }

    /**
     * Load the styles cache from the array of style entities.
     * @param {Array} entities
     */
    loadStyleCache (entities) {
        //Store the cache that was returned...EK
        if (Array.isArray(entities)) {
            /**
             * Shortcut reference to the all styles dictionary object
             * @type {Dictionary}
             */
            let cStyles = this.styles.allStyles;

            entities.forEach(
                /**
                 * Create a Style entity from the raw JSON object and populate the cache
                 * @param {Object} styleEntity
                 */
                (styleEntity) => {
                    if (typeof styleEntity.Definition === 'string') {
                        styleEntity.Definition = JSON.parse(styleEntity.Definition);
                    }

                    /**
                     * Hydrate the style entity using the raw JSON object as "defaults"
                     * @type {Style}
                     */
                    let styleObj = new Style(styleEntity);

                    // add the new style entity to the dictionary
                    cStyles.add(styleObj.StyleID, styleObj, styleObj.UID);
                });
        }
    }

    /**
     * Load the value status and value status by value caches from the array of value status objects.
     * @param {Array} entities
     */
    loadValueStatusCache (entities) {
        // Populate the cached Value Statuses..PW
        if (Array.isArray(entities)) {
            /**
             * Get shortcut reference to the value statuses dictionary
             * @type {Dictionary}
             */
            let cValueStatuses = this.valueStatuses.allValueStatuses,

                /**
                 * Get shortcut reference to the dictionary of value status entities by status value
                 * @type {Dictionary}
                 */
                cValueStatusesByValue = this.valueStatuses.valueStatusByValue;

            entities.forEach(
                /**
                 * Create a Value Status entity from the raw JSON object and populate the cache
                 * @param {Object} valueStatusEntity
                 */
                (valueStatusEntity) => {
                    if (typeof valueStatusEntity.Definition === 'string') {
                        valueStatusEntity.Definition = JSON.parse(valueStatusEntity.Definition);
                    }

                    // apply client properties
                    this.valueStatuses.applyClientProperties(valueStatusEntity);

                    // add the object to the cache
                    cValueStatuses.add(valueStatusEntity.ValueStatusID, valueStatusEntity, valueStatusEntity.UID);

                    // add the object to the cache by its actual status value
                    cValueStatusesByValue.add(valueStatusEntity.StatusValue, valueStatusEntity, valueStatusEntity.UID);
                });

            // Setup the not initialized and marked as deleted statuses
            this.valueStatuses.setStoredStatuses();
        }
    }

    /**
     * Load the extension cache from the array of extension objects.
     * @param {Array} entities
     */
    loadExtensionCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the extensions cache
             */
            let cExtensions = this.extensions;

            entities.forEach(
                /**
                 * Create a Extension entity from the raw JSON object and populate the cache
                 * @param {EntityBase} extensionEntity
                 */
                (extensionEntity) => {
                    if (typeof extensionEntity.Definition === 'string') {
                        extensionEntity.Definition = JSON.parse(extensionEntity.Definition);
                    }

                    /**
                     * By definition, this is a custom entity
                     * @type {boolean}
                     */
                    extensionEntity.isCustom = true;

                    switch (extensionEntity.Type) {
                        case "Expression":
                            // prepare, add to cache, and register the custom expression entity
                            cExtensions.applyExpressionClientProperties(extensionEntity);
                            cExtensions.allExpressions.add(extensionEntity.ExtensionID, extensionEntity, extensionEntity.UID);
                            cExtensions.registerExpression(extensionEntity);
                            break;
                        case "Action":
                            // prepare, add to cache, and register the custom action entity
                            cExtensions.applyActionClientProperties(extensionEntity);
                            cExtensions.allActions.add(extensionEntity.ExtensionID, extensionEntity, extensionEntity.UID);
                            cExtensions.registerAction(extensionEntity);
                            break;
                        case "Widget":
                            // prepare, add to cache, and register the custom widget entity
                            cExtensions.applyWidgetClientProperties(extensionEntity);
                            cExtensions.allWidgets.add(extensionEntity.ExtensionID, extensionEntity, extensionEntity.UID);
                            cExtensions.registerWidget(extensionEntity);
                            break;
                        case "Endpoint":
                            // prepare and add to cache the custom endpoint entity
                            cExtensions.applyEndpointClientProperties(extensionEntity);
                            cExtensions.allCustomEndpoints.add(extensionEntity.ExtensionID, extensionEntity, extensionEntity.UID);
                            break;
                        case "Adapter":
                            // we do not handle adapter entities at this time.
                            break;

                        default:
                            break;
                    }
                });
        }    }

    /**
     * Load the view cache from the array of view entities.
     * @param {Array} entities
     */
    loadViewCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the view entity dictionary
             * @type {Dictionary}
             */
            let cViews = this.views.allViews;

            entities.forEach(
                /**
                 * Create a View entity from the raw JSON object and populate the cache
                 * @param {Object} viewEntity
                 */
                (viewEntity) => {
                    if (typeof viewEntity.Definition === 'string') {
                        viewEntity.Definition = JSON.parse(viewEntity.Definition);
                    }

                    this.views.applyClientProperties(viewEntity);
                    cViews.add(viewEntity.ViewID, viewEntity, viewEntity.UID);

                    // if this is a pending view, we need to keep the clientProperties in sync.
                    let origView = this.views.allOriginalViews.getVal(viewEntity.ViewID);
                    if (origView != null) {
                        origView.clientProperties = viewEntity.clientProperties;
                    }

                });
        }
    }

    /**
     * Load the security cache from the array of security entities
     * @param {Array} entities
     */
    loadSecurityCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the security entities dictionary
             * @type {Dictionary}
             */
            let cSecurities = this.securities.allSecurities;

            entities.forEach(
                /**
                 * Create a Security entity from the raw JSON object and populate the cache
                 * @param {Object} securityEntity
                 */
                (securityEntity) => {
                    cSecurities.add(securityEntity.SecurityID, securityEntity, securityEntity.UID);
                });
        }
    }

    /**
     * Load the security group cache from the array of security group entities
     * @param {Array} entities
     */
    loadSecurityGroupCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the security group entity dictionary
             * @type {Dictionary}
             */
            let cSecurityGroup = this.securityGroups.allSecurityGroups;

            entities.forEach(
                /**
                 * Create a Security entity from the raw JSON object and populate the cache
                 * @param {Object} securityGroupEntity
                 */
                (securityGroupEntity) => {
                    if (typeof securityGroupEntity.Definition === 'string') {
                        securityGroupEntity.Definition = JSON.parse(securityGroupEntity.Definition);
                    }

                    this.securityGroups.applyClientProperties(securityGroupEntity);
                    cSecurityGroup.add(securityGroupEntity.SecurityGroupID, securityGroupEntity, securityGroupEntity.UID);
                });
        }
    }

    /**
     * Load the security group lookup cache from the array of security group lookup entities.
     * @param {Array} entities
     */
    loadSecurityGroupLookupCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the security group lookup entity dictionary
             * @type {Dictionary}
             */
            let cSecurityGroupLookups = this.securityGroupLookups.allSecurityGroupLookups;

            entities.forEach(
                /**
                 * Create a Security Group Lookup entity from the raw JSON object and populate the cache
                 * @param {Object} securityGroupLookupEntity
                 */
                (securityGroupLookupEntity) => {
                    cSecurityGroupLookups.add(securityGroupLookupEntity.SecurityGroupLookupID, securityGroupLookupEntity, securityGroupLookupEntity.UID);
                });
        }
    }

    /**
     * Load the package cache from the array of package entities.
     * @param {Array} entities
     */
    loadPackageCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the package entity dictionary
             * @type {Dictionary}
             */
            let cPackages = this.packages.allPackages;

            entities.forEach(
                /**
                 * Create a Package entity from the raw JSON object and populate the cache
                 * @param {Object} packageEntity
                 */
                (packageEntity) => {
                    if (typeof packageEntity.Definition === 'string') {
                        packageEntity.Definition = JSON.parse(packageEntity.Definition);
                    }

                    this.packages.applyClientProperties(packageEntity);
                    cPackages.add(packageEntity.PackageID, packageEntity, packageEntity.UID);
                });
        }
    }

    /**
     * Load the tag cache from the array of tag entities.
     * @param {Array} entities
     */
    loadTagCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the tags entity dictionary
             * @type {Dictionary}
             */
            let cTags = this.tags.allTags;

            entities.forEach(
                /**
                 * Create a Tag entity from the raw JSON object and populate the cache
                 * @param {Object} tag
                 */
                (tag) => {
                    if (typeof tag.Definition === 'string') {
                        tag.Definition = JSON.parse(tag.Definition);
                    }

                    this.tags.applyClientProperties(tag);
                    cTags.add(tag.TagID, tag, tag.UID);
                });
        }
    }

    /**
     * Load the tag link cache from the array of tag link entities.
     * @param {Array} entities
     */
    loadTagLinkCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference to the tag link entity dictionary
             * @type {Dictionary}
             */
            let cTagLinks = this.tags.allTagLinks;

            entities.forEach(
                /**
                 * Create a Tag Link entity from the raw JSON object and populate the cache
                 * @param {Object} tagLinkEntity
                 */
                (tagLinkEntity) => {
                    if (typeof tagLinkEntity.Definition === 'string') {
                        tagLinkEntity.Definition = JSON.parse(tagLinkEntity.Definition);
                    }

                    //TODO: Remove this after we are sure that there are no "all caps" Entity UID values in any database instance!!! -EK
                    tagLinkEntity.EntityUID = tagLinkEntity.EntityUID.toLowerCase();
                    cTagLinks.add(tagLinkEntity.TagLinkID, tagLinkEntity, tagLinkEntity.UID);
                });
        }
    }

    /**
     * Load the pending change cache from the array of pending change entities
     * @param {Array} entities
     */
    loadPendingChangeCache (entities) {
        if (Array.isArray(entities)) {
            /**
             * Get a shortcut reference for the pending change entity dictionary
             * @type {Dictionary}
             */
            let cPendingChanges = this.pendingChanges.allPendingChanges;

            entities.forEach(
                /**
                 * Create a Pending Change entity from the raw JSON object and populate the cache
                 * @param {Object} pendingChangeEntity
                 */
                (pendingChangeEntity) => {
                    // The definition may not always be supplied; if it is and it is a string, JSON.parse() it.
                    if (pendingChangeEntity.Definition != null && typeof pendingChangeEntity.Definition === 'string') {
                        pendingChangeEntity.Definition = JSON.parse(pendingChangeEntity.Definition);
                    }

                    if (typeof pendingChangeEntity.Metadata === 'string') {
                        pendingChangeEntity.Metadata = JSON.parse(pendingChangeEntity.Metadata);
                    }

                    this.pendingChanges.applyClientProperties(pendingChangeEntity);
                    cPendingChanges.add(pendingChangeEntity.PendingChangeID, pendingChangeEntity, pendingChangeEntity.UID);
                });
        }
    }

    /**
     * Load cache from a snapshot of cache state, if available
     */
    loadSnapshot () {
        if (snapshot.isActive) {
            // This client has a snapshot (cordova build), import all configuration in the snapshot...EK
            this.recordTypes.mergeCache(snapshot.recordTypes || []);
            this.categories.mergeCache(snapshot.categories || []);
            this.properties.mergeCache(snapshot.properties || []);
            this.loadValueStatusCache(snapshot.valueStatuses || []);
            this.loadViewCache(snapshot.views || []);
            this.loadSecurityCache(snapshot.securities || []);
            this.loadSecurityGroupCache(snapshot.securityGroups || []);
            this.loadSecurityGroupLookupCache(snapshot.securityGroupLookups || []);
            this.loadPackageCache(snapshot.packages || []);
            this.loadStyleCache(snapshot.styles || []);
            this.loadTagCache(snapshot.tags || []);
            this.loadTagLinkCache(snapshot.tagLinks || []);
            this.loadExtensionCache(snapshot.extensions || []);

            this.organizeEntityCache();
        }
    }
}

export const cache = new Cache();
